import React, { useEffect, useState, useMemo } from 'react';
import { Space, Typography, Row, Col, Form, Input, Tag, Button, Select, Table } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import BeaconSelector from './BeaconSelector';
import CampaignRequestorInventoryDialog from '../../CampaignRequestor/CampaignRequestorInventoryDialog';
import CampaignRequestorNexxenSegmentsDialog from '../../CampaignRequestor/CampaignRequestorNexxenSegmentsDialog';
import { useCampaignRequestorContext } from '../../../../core/components/campaignRequestor/useCampaignRequestorContext';
import { sspPartners } from '../../../../core/utils/constants/constants';

const { Title, Text } = Typography;

export const CampaignOrderQueueReviewCampaignLaunchDetails = ({
  form,
  campaignIndex,
  lineItemIndex,
  fieldName,
  isLaunching = false,
}) => {
  const [showInventoryDialog, setShowInventoryDialog] = useState(false);
  const [showNexxenSegmentsDialog, setShowNexxenSegmentsDialog] = useState(false);
  const [sspOtions, setSspOptions] = useState([]);
  const campaign = form.getFieldValue('campaigns')[campaignIndex];
  const lineItem = campaign?.lineItems?.[lineItemIndex];

  const { currentCampaignOrder, selectedAdvertiser } = useCampaignRequestorContext();

  // Determine pricing model
  const pricingModel = useMemo(() => {
    // Priority 1: Use pricingModel directly from campaignOrder (string value)
    let model = currentCampaignOrder?.pricingModel;

    // Priority 2: Try selectedAdvertiser.pricingModel.model
    if (!model && selectedAdvertiser?.pricingModel?.model) {
      model = selectedAdvertiser.pricingModel.model;
    }

    // Priority 3: Try currentCampaignOrder.advertiser.pricingModel.model
    if (!model && currentCampaignOrder?.advertiser?.pricingModel?.model) {
      model = currentCampaignOrder.advertiser.pricingModel.model;
    }

    if (!model) {
      return null;
    }

    if (model === 'Percentage') {
      return {
        name: 'Percentage',
        description: 'Budget-based pricing',
        color: 'purple',
        tooltip: 'This campaign uses percentage-based pricing where costs are calculated as a percentage of the budget allocation.'
      };
    }

    if (model === 'CPM') {
      return {
        name: 'CPM',
        description: 'Impression-based pricing',
        color: 'blue',
        tooltip: 'This campaign uses CPM (Cost Per Mille) pricing where costs are based on impressions delivered.'
      };
    }

    return null;
  }, [selectedAdvertiser, currentCampaignOrder]);

  // Get beacons from actual campaign order data for display
  const savedBeacons = currentCampaignOrder?.campaigns?.[campaignIndex]?.lineItems?.[lineItemIndex]?.beacons;

  // Check if this specific line item has been launched (has dspLineItemId)
  const currentLineItem = currentCampaignOrder?.campaigns?.[campaignIndex]?.lineItems?.[lineItemIndex];
  const isLineItemLaunched = !!currentLineItem?.dspLineItemId;

  // Disable fields if:
  // 1. Campaign status is LIVE or PAUSED AND
  // 2. This specific line item has been launched (has dspLineItemId)
  const campaignStatus = currentCampaignOrder?.campaigns?.[campaignIndex]?.status;
  const isFieldDisabled = (['LIVE', 'PAUSED'].includes(campaignStatus) && isLineItemLaunched) || isLaunching;

  // Detect UPDATE mode: campaign already launched with adProviderId
  const isUpdateMode = !!(campaign?.adProviderId && campaign.adProviderId.trim() !== '');

  // Check if this line item is marked for delivery and needs DSP Line Item ID
  const isMarkedForDelivery = currentLineItem?.markedForDelivery === true;
  const isLineItemReadyForUpdate = isUpdateMode && isMarkedForDelivery && !isLineItemLaunched;

  // Check if DSP is Nexxen and transaction tags are present
  const dspType = campaign?.demandSidePlatform;
  const hasTransactionTags = useMemo(() => {
    const transactionsConfig = currentCampaignOrder?.transactionsConfig;
    const generatedTag = transactionsConfig?.tag?.generatedTag;

    if (!generatedTag || typeof generatedTag !== 'string') {
      return false;
    }

    // Check for s2.pv or s2.tr in the generated tag
    return generatedTag.includes('s2.pv=') || generatedTag.includes('s2.tr=');
  }, [currentCampaignOrder]);

  // Check if line item has action-based bidding
  const hasActionBasedBidding = useMemo(() => {
    // This logic should match the external service's detection logic
    // You may need to adjust based on your actual bidding configuration structure
    return lineItem?.targetingType === 'ACTION_BASED' ||
           lineItem?.biddingStrategy === 'MAXIMIZE_ACTIONS';
  }, [lineItem]);

  // Get current Nexxen deals and segments from actual campaign order data (like savedBeacons)
  // First try form state, then fall back to saved data from currentCampaignOrder
  // Note: segments can be stored as either 'nexxenSegments' or 'segments' depending on the source
  const savedLineItem = currentCampaignOrder?.campaigns?.[campaignIndex]?.lineItems?.[lineItemIndex];
  const savedNexxenDeals = savedLineItem?.dspDealIds;
  const savedNexxenSegments = savedLineItem?.nexxenSegments || savedLineItem?.segments;
  const savedInventorySources = savedLineItem?.inventorySources;
  const savedInventoryLists = savedLineItem?.inventoryLists;
  const currentNexxenDeals = lineItem?.dspDealIds || savedNexxenDeals || [];
  const currentNexxenSegments = lineItem?.nexxenSegments || lineItem?.segments || savedNexxenSegments || [];
  const currentInventorySources = lineItem?.inventorySources || savedInventorySources || [];
  const currentInventoryLists = lineItem?.inventoryLists || savedInventoryLists || [];

  // Handle saving inventory config (sources, lists, deals) from unified dialog
  const handleSaveInventoryConfig = ({ sources, lists, deals }) => {
    const campaigns = form.getFieldValue('campaigns') || [];
    const updatedCampaigns = [...campaigns];

    if (updatedCampaigns[campaignIndex]?.lineItems?.[lineItemIndex]) {
      updatedCampaigns[campaignIndex].lineItems[lineItemIndex].inventorySources = sources;
      updatedCampaigns[campaignIndex].lineItems[lineItemIndex].inventoryLists = lists;
      updatedCampaigns[campaignIndex].lineItems[lineItemIndex].dspDealIds = deals;
      form.setFieldsValue({ campaigns: updatedCampaigns });
    }
    setShowInventoryDialog(false);
  };

  // Handle removing a Nexxen deal
  const handleRemoveNexxenDeal = (dealId) => {
    const campaigns = form.getFieldValue('campaigns') || [];
    const updatedCampaigns = [...campaigns];

    if (updatedCampaigns[campaignIndex]?.lineItems?.[lineItemIndex]) {
      const currentDeals = updatedCampaigns[campaignIndex].lineItems[lineItemIndex].dspDealIds || [];
      updatedCampaigns[campaignIndex].lineItems[lineItemIndex].dspDealIds = currentDeals.filter(
        (deal) => deal.dealId !== dealId
      );
      form.setFieldsValue({ campaigns: updatedCampaigns });
    }
  };

  // Handle removing an inventory source
  const handleRemoveInventorySource = (sourceId) => {
    const campaigns = form.getFieldValue('campaigns') || [];
    const updatedCampaigns = [...campaigns];

    if (updatedCampaigns[campaignIndex]?.lineItems?.[lineItemIndex]) {
      const currentSources = updatedCampaigns[campaignIndex].lineItems[lineItemIndex].inventorySources || [];
      updatedCampaigns[campaignIndex].lineItems[lineItemIndex].inventorySources = currentSources.filter(
        (source) => source.sourceId !== sourceId
      );
      form.setFieldsValue({ campaigns: updatedCampaigns });
    }
  };

  // Handle removing an inventory list
  const handleRemoveInventoryList = (listId) => {
    const campaigns = form.getFieldValue('campaigns') || [];
    const updatedCampaigns = [...campaigns];

    if (updatedCampaigns[campaignIndex]?.lineItems?.[lineItemIndex]) {
      const currentLists = updatedCampaigns[campaignIndex].lineItems[lineItemIndex].inventoryLists || [];
      updatedCampaigns[campaignIndex].lineItems[lineItemIndex].inventoryLists = currentLists.filter(
        (list) => list.listId !== listId
      );
      form.setFieldsValue({ campaigns: updatedCampaigns });
    }
  };

  // Handle adding Nexxen segment
  const handleAddNexxenSegment = (selectedSegments) => {
    const campaigns = form.getFieldValue('campaigns') || [];
    const updatedCampaigns = [...campaigns];

    if (updatedCampaigns[campaignIndex]?.lineItems?.[lineItemIndex]) {
      // Save to both field names for compatibility
      updatedCampaigns[campaignIndex].lineItems[lineItemIndex].nexxenSegments = selectedSegments;
      updatedCampaigns[campaignIndex].lineItems[lineItemIndex].segments = selectedSegments;
      form.setFieldsValue({ campaigns: updatedCampaigns });
    }
    setShowNexxenSegmentsDialog(false);
  };

  // Handle removing a Nexxen segment
  const handleRemoveNexxenSegment = (segmentId) => {
    const campaigns = form.getFieldValue('campaigns') || [];
    const updatedCampaigns = [...campaigns];

    if (updatedCampaigns[campaignIndex]?.lineItems?.[lineItemIndex]) {
      const currentSegments = updatedCampaigns[campaignIndex].lineItems[lineItemIndex].nexxenSegments ||
                              updatedCampaigns[campaignIndex].lineItems[lineItemIndex].segments || [];
      const filteredSegments = currentSegments.filter(
        (segment) => segment.segmentId !== segmentId
      );
      // Save to both field names for compatibility
      updatedCampaigns[campaignIndex].lineItems[lineItemIndex].nexxenSegments = filteredSegments;
      updatedCampaigns[campaignIndex].lineItems[lineItemIndex].segments = filteredSegments;
      form.setFieldsValue({ campaigns: updatedCampaigns });
    }
  };

  // Nexxen Deals table columns
  const nexxenDealsColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
    },
    {
      title: 'Deal ID',
      dataIndex: 'dealId',
      key: 'dealId',
      width: 200,
    },
    {
      title: 'CPM Bid',
      dataIndex: 'cpmBid',
      key: 'cpmBid',
      width: 100,
      render: (value) => (value ? `$${Number(value).toFixed(2)}` : '-'),
    },
  ];

  // Nexxen Segments table columns
  const nexxenSegmentsColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
    },
    {
      title: 'Type',
      dataIndex: 'segmentType',
      key: 'segmentType',
      width: 120,
    },
    {
      title: 'Cost',
      dataIndex: 'cost',
      key: 'cost',
      width: 100,
      render: (value) => (value ? `$${Number(value).toFixed(2)}` : '-'),
    },
  ];

  // Inventory Sources table columns
  const inventorySourcesColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
    },
  ];

  // Inventory Lists table columns
  const inventoryListsColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 100,
      render: (action) => (
        <Tag color={action === 'include' ? 'green' : 'red'}>
          {action === 'include' ? 'Include' : 'Exclude'}
        </Tag>
      ),
    },
  ];

  useEffect(() => {
    setSspOptions(
      sspPartners.map((value) => ({
        value,
        label: value,
      }))
    );
  }, []);

  // Set dspLineItemId field value when data loads
  useEffect(() => {
    if (isUpdateMode && currentLineItem?.dspLineItemId) {
      // fieldName is already an array path, construct the full field path
      const fieldPath = Array.isArray(fieldName)
        ? [...fieldName, 'dspLineItemId']
        : [fieldName, 'dspLineItemId'];

      const currentFieldValue = form.getFieldValue(fieldPath);

      if (!currentFieldValue) {
        // Get the full campaigns array and update the specific line item
        const campaigns = form.getFieldValue('campaigns') || [];
        const updatedCampaigns = [...campaigns];

        if (updatedCampaigns[campaignIndex]?.lineItems?.[lineItemIndex]) {
          updatedCampaigns[campaignIndex].lineItems[lineItemIndex].dspLineItemId = currentLineItem.dspLineItemId;
          form.setFieldsValue({ campaigns: updatedCampaigns });
        }
      }
    }
  }, [isUpdateMode, campaignIndex, lineItemIndex, form, fieldName, currentLineItem.dspLineItemId]);

  return (
    <>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Row>
          <Col span={12}>
            <Title level={5}>LAUNCH DETAILS</Title>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text>SSP</Text>
              <Text type="secondary">
                <small> Supply-Side Platform </small>
              </Text>
              <Form.Item
                name={[fieldName, 'supplySidePlatform']}
                rules={[
                  {
                    required: true,
                    message: 'Please select a SSP',
                  },
                ]}
              >
                <Select
                  style={{ width: '100%' }}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  options={sspOtions}
                  disabled={isFieldDisabled}
                />
              </Form.Item>
            </Space>
          </Col>
          {isUpdateMode && (
            <Col span={6}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>DSP Line Item ID</Text>
                <Text type="secondary">
                  <small>{isLineItemReadyForUpdate ? 'Required for manual update' : 'Line item ID from DSP'}</small>
                </Text>
                <Form.Item
                  name={[fieldName, 'dspLineItemId']}
                  rules={[
                    {
                      pattern: /^[a-zA-Z0-9_-]*$/,
                      message: 'Only alphanumeric characters, hyphens, and underscores allowed',
                    },
                  ]}
                >
                  <Input
                    style={{ width: '100%' }}
                    placeholder="Enter DSP line item ID"
                    disabled={isFieldDisabled}
                  />
                </Form.Item>
              </Space>
            </Col>
          )}
        </Row>
        <Row gutter={[16, 16]}>
          {/* Only show Maximum Bid if pricing model is not Percentage */}
          {pricingModel?.name !== 'Percentage' && (
            <Col span={6}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>Maximum Bid</Text>
                <Text type="secondary">
                  <small> Max bid per CPM 1</small>
                </Text>
                <Form.Item
                  name={[fieldName, 'maxBid']}
                  rules={[
                    {
                      required: true,
                      message: 'Please input a maximum bid',
                    },
                  ]}
                >
                  <Input
                    addonBefore="$USD"
                    type="number"
                    style={{ width: '75%' }}
                    disabled={isFieldDisabled}
                  />
                </Form.Item>
              </Space>
            </Col>
          )}
          <Col span={6}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text>Frequency Cap</Text>
              <Text type="secondary">
                <small> Max daily impressions per device </small>
              </Text>
              <Form.Item
                name={[fieldName, 'frequencyCap']}
                rules={[{ required: true, message: 'Please input a frequency cap' }]}
              >
                <Input
                  type="number"
                  style={{ width: '75%' }}
                  disabled={isFieldDisabled}
                />
              </Form.Item>
            </Space>
          </Col>
        </Row>

        {/* Inventory & Supply - Only show for Nexxen DSP */}
        {dspType === 'NEXXEN' && (
          <Row>
            <Col span={24}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>Inventory & Supply</Text>
                <Text type="secondary">
                  <small>Configure inventory sources, site lists, and private deals for this line item</small>
                </Text>

                {/* Inventory Sources Table */}
                {currentInventorySources.length > 0 && (
                  <>
                    <Text strong style={{ fontSize: 13 }}>Inventory Sources (SSPs/Exchanges)</Text>
                    <Table
                      dataSource={currentInventorySources}
                      columns={inventorySourcesColumns}
                      rowKey="sourceId"
                      size="small"
                      pagination={false}
                      style={{ marginBottom: 8 }}
                    />
                  </>
                )}

                {/* Inventory Lists Table */}
                {currentInventoryLists.length > 0 && (
                  <>
                    <Text strong style={{ fontSize: 13 }}>Inventory Lists (Site/App Lists)</Text>
                    <Table
                      dataSource={currentInventoryLists}
                      columns={inventoryListsColumns}
                      rowKey="listId"
                      size="small"
                      pagination={false}
                      style={{ marginBottom: 8 }}
                    />
                  </>
                )}

                {/* Publisher Deals Table */}
                {currentNexxenDeals.length > 0 ? (
                  <>
                    <Text strong style={{ fontSize: 13 }}>Publisher Deals</Text>
                    <Table
                      dataSource={currentNexxenDeals}
                      columns={nexxenDealsColumns}
                      rowKey="dealId"
                      size="small"
                      pagination={false}
                      style={{ marginBottom: 8 }}
                    />
                  </>
                ) : (
                  !currentInventorySources.length && !currentInventoryLists.length && (
                    <Text type="secondary" style={{ fontStyle: 'italic', display: 'block', marginBottom: 8 }}>
                      No inventory configured (will use defaults)
                    </Text>
                  )
                )}

              </Space>
            </Col>
          </Row>
        )}

        {/* Nexxen Segments - Only show for Nexxen DSP */}
        {dspType === 'NEXXEN' && (
          <Row>
            <Col span={24}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>Nexxen Segments</Text>
                <Text type="secondary">
                  <small>Optional: Select a Nexxen segment set for audience targeting (single selection)</small>
                </Text>
                {currentNexxenSegments.length > 0 ? (
                  <Table
                    dataSource={currentNexxenSegments}
                    columns={nexxenSegmentsColumns}
                    rowKey="segmentId"
                    size="small"
                    pagination={false}
                    style={{ marginBottom: 8 }}
                  />
                ) : (
                  <Text type="secondary" style={{ fontStyle: 'italic', display: 'block', marginBottom: 8 }}>
                    No Nexxen segment selected
                  </Text>
                )}
              </Space>
            </Col>
          </Row>
        )}

        {/* Beacon Selector - Only show for Nexxen DSP with transaction tags */}
        {dspType === 'NEXXEN' && hasTransactionTags && (
          <Row>
            <Col span={24}>
              {isFieldDisabled ? (
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text>Beacons</Text>
                  <Text type="secondary">
                    <small>Attached beacons for conversion tracking</small>
                  </Text>
                  <div style={{ marginTop: 8 }}>
                    {savedBeacons && savedBeacons.length > 0 ? (
                      <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        {savedBeacons.map((beacon, index) => (
                          <div key={`${beacon.beaconId}-${index}`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Tag
                              color={beacon.beaconType === 'TRANSACTION' ? 'green' :
                                     beacon.beaconType === 'PAGE_VIEW' ? 'blue' : 'default'}
                              style={{ margin: 0, minWidth: 120, textAlign: 'center' }}
                            >
                              {beacon.beaconType === 'TRANSACTION' ? 'Transaction' :
                               beacon.beaconType === 'PAGE_VIEW' ? 'Page View' :
                               beacon.beaconType || 'Standard'}
                            </Tag>
                            <Text>{beacon.name}</Text>
                          </div>
                        ))}
                      </Space>
                    ) : (
                      <Text type="secondary" style={{ fontStyle: 'italic' }}>
                        No beacons attached
                      </Text>
                    )}
                  </div>
                </Space>
              ) : (
                <Form.Item
                  name={[fieldName, 'beacons']}
                  style={{ marginBottom: 0 }}
                >
                  <BeaconSelector
                    advertiserId={currentCampaignOrder?.advertiser?.dataConfig?.dspAdvertiserId}
                    lineItemId={lineItem?.id}
                    lineItemName={lineItem?.name}
                    dspType={dspType}
                    hasTransactionTags={hasTransactionTags}
                    transactionTags={currentCampaignOrder?.transactionsConfig?.tag?.generatedTag}
                    hasActionBasedBidding={hasActionBasedBidding}
                    form={form}
                    fieldName={[fieldName, 'beacons']}
                    disabled={isFieldDisabled}
                  />
                </Form.Item>
              )}
            </Col>
          </Row>
        )}
      </Space>

      {showInventoryDialog && (
        <CampaignRequestorInventoryDialog
          visible={showInventoryDialog}
          setVisible={setShowInventoryDialog}
          selectedInventorySources={currentInventorySources}
          selectedInventoryLists={currentInventoryLists}
          selectedPublisherDeals={currentNexxenDeals}
          campaignType={lineItem?.campaignType}
          onSaveInventoryConfig={handleSaveInventoryConfig}
        />
      )}

      {showNexxenSegmentsDialog && (
        <CampaignRequestorNexxenSegmentsDialog
          visible={showNexxenSegmentsDialog}
          setVisible={setShowNexxenSegmentsDialog}
          selectedSegments={currentNexxenSegments}
          onAddSelections={handleAddNexxenSegment}
        />
      )}
    </>
  );
};
